import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { GetRentalApplicationDocument, GetTermSetDocument, TermId, TermSetId } from '../../graphql/queries';
import type { UserStatus } from '../../graphql/queries';
import { TallHeightLoadingMask } from '../../styles/styled-components';
import { ApplicationFullPage } from '../../components/ApplicationFullPage/ApplicationFullPage';
import { ApplicationLanding } from '../../components/ApplicationLanding';
import { ErrorPage } from '../../components/Error/ErrorPage';
import { ROUTES } from '../../constants/urls';
import { logError } from '@zg-rentals/log-error';
import { useEffect, useMemo, useState } from 'react';
import type { FeatureFlags } from '../../contexts/ApplicationContext';
import { ApplicationContextProvider } from '../../contexts/ApplicationContext';
import { usePaymentRequestTreatment } from '../../utils/ab-tests';
import { useRenterRequestedZillowPayments } from '../../utils/rentPaymentQueries';
import { FIELD_IDS } from '../../constants/wizard';

type QueryProps = {
  applicationId: string;
  stepId?: string;
};

export default function ApplicationPage() {
  const router = useRouter();
  const { applicationId, stepId } = router.query as QueryProps;
  const {
    data: rentalApplicationDataWithoutRequestPayments,
    loading: rentalApplicationDataLoading,
    error,
  } = useQuery(GetRentalApplicationDocument, { variables: { applicationId } });

  const { data: termSetData, loading: termSetDataLoading } = useQuery(GetTermSetDocument, {
    variables: { termSetId: TermSetId.renterScreening },
  });

  const zillowRentalUserTerm = termSetData?.termSet?.terms?.find(
    (term) => term?.id === TermId.TERMS_ZILLOW_RENTAL_USER,
  );
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

  const paymentRequestTreatment = usePaymentRequestTreatment();
  const paymentRequestStatus = useRenterRequestedZillowPayments(applicationId, {
    enabled: paymentRequestTreatment != null && paymentRequestTreatment !== 'off',
  });

  // useMemo used for the stability of the rentalApplicationData instance, not for performance reasons
  // otherwise the instance will be recreated on every render possibly retriggering hooks on child components
  const rentalApplicationData = useMemo(
    () =>
      rentalApplicationDataWithoutRequestPayments && {
        ...rentalApplicationDataWithoutRequestPayments,
        rentalApplication: {
          ...rentalApplicationDataWithoutRequestPayments?.rentalApplication,
          answers: {
            ...rentalApplicationDataWithoutRequestPayments?.rentalApplication?.answers,
            [FIELD_IDS.REQUEST_ZILLOW_PAYMENTS]: paymentRequestStatus,
          },
        },
      },
    [rentalApplicationDataWithoutRequestPayments, paymentRequestStatus],
  );

  const featureFlags: FeatureFlags | undefined =
    paymentRequestTreatment != null
      ? {
          paymentRequestTreatment,
        }
      : undefined;

  useEffect(() => {
    if (zillowRentalUserTerm == null) return;

    if (zillowRentalUserTerm.acceptedVersion === -1) {
      // If the user has not accepted the user terms, this is a "first time user"
      // We use a state to preserve the flag after the user accepts the terms and conditions, as long as this
      // component is not unmounted, it will continue to be considered as a first time user throughout the application
      setIsFirstTimeUser(true);
    }
  }, [zillowRentalUserTerm]);

  if (error) {
    logError({ error, context: 'ApplicationPage - GetRentalApplicationDocument error' });
    return (
      <ErrorPage
        errorMessage="There was an error loading your application."
        homePageText={'Return to your applications dashboard'}
        homePageUrl={ROUTES.INDEX}
      />
    );
  }

  if (rentalApplicationDataLoading || termSetDataLoading) {
    return (
      <TallHeightLoadingMask>
        <></>
      </TallHeightLoadingMask>
    );
  }

  if (zillowRentalUserTerm && zillowRentalUserTerm.acceptedVersion !== -1) {
    return (
      rentalApplicationData?.rentalApplication &&
      rentalApplicationData.userStatuses &&
      termSetData?.termSet?.status &&
      // We delay the rendering of the application until the feature flags object
      // is available since the application steps visibility depend on them
      featureFlags &&
      // If the payment request treatment is ON we also wait for the payment request status
      (featureFlags.paymentRequestTreatment === 'off' || paymentRequestStatus !== undefined) && (
        <ApplicationContextProvider
          application={rentalApplicationData.rentalApplication}
          userStatuses={rentalApplicationData.userStatuses as Array<UserStatus>}
          isFirstTimeUser={isFirstTimeUser}
          termSetStatus={termSetData.termSet.status}
          featureFlags={featureFlags}
        >
          <ApplicationFullPage stepId={stepId} />
        </ApplicationContextProvider>
      )
    );
  } else {
    return <ApplicationLanding applicationId={applicationId} />;
  }
}
